'use client';

import { AppleQR } from '@/components/ui/AppleQR';
import { Modal, ModalDescription, ModalHeader, ModalTitle } from '@/components/ui/Modal';
import { Button } from '@/components/ui/button';
import { SFIcon } from '@/components/ui/sf-icon';
import { toast } from '@/hooks/use-toast';
import { sfIcons } from '@/lib/sf-icons';
import { cn } from '@/lib/utils';
import { semanticTokens } from '@packages/config/theme';
import { useState } from 'react';

interface QRCodeDialogProps {
  address: string;
  asset?: string;
  chain?: string;
  label?: string;
  trigger?: React.ReactNode;
}

export const QRCodeDialog = ({ address, asset, chain, label, trigger }: QRCodeDialogProps) => {
  const [open, setOpen] = useState(false);

  // Normalize asset to uppercase (USDC, not usdc)
  const displayAsset = asset?.toUpperCase() || 'Crypto';
  const displayChain = chain ? chain.charAt(0).toUpperCase() + chain.slice(1).toLowerCase() : '';

  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(address);
      toast({
        title: "Copied!",
        description: "Address copied to clipboard"
      });
    } catch (error) {
      toast({
        title: "Copy failed",
        description: "Please copy manually",
        variant: "destructive"
      });
    }
  };

  const shareAddress = async () => {
    const shareData = {
      title: `${displayAsset} Address`,
      text: `Send ${displayAsset} to this address${displayChain ? ` on ${displayChain}` : ''}: ${address}`,
      url: undefined // Some apps expect URL field
    };

    try {
      // Check if native share is available (mobile/PWA)
      if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData);
        toast({
          title: "Shared!",
          description: "Address shared successfully"
        });
      } else if (navigator.share) {
        // Fallback for older browsers that support share but not canShare
        await navigator.share(shareData);
      } else {
        // Desktop fallback - copy to clipboard
        await navigator.clipboard.writeText(`${displayAsset} Address${displayChain ? ` (${displayChain})` : ''}: ${address}`);
        toast({
          title: "Copied to clipboard",
          description: "Native sharing not available. Address copied instead."
        });
      }
    } catch (error) {
      // User cancelled share or error occurred
      if ((error as Error).name !== 'AbortError') {
        // Only show error if not user cancellation
        await navigator.clipboard.writeText(address);
        toast({
          title: "Copied to clipboard",
          description: "Address copied to clipboard"
        });
      }
    }
  };

  return (
    <>
      <div onClick={() => setOpen(true)}>
        {trigger || (
          <Button variant="outline" size="sm" className="gap-2">
            <SFIcon icon={sfIcons.fingerprint} size="sm" />
            QR Code
          </Button>
        )}
      </div>

      <Modal
        open={open}
        onOpenChange={setOpen}
        size="sm"
        aria-labelledby="qr-title"
        aria-describedby="qr-description"
      >
        <ModalHeader>
          <ModalTitle id="qr-title" className="flex items-center gap-2">
            <SFIcon icon={sfIcons.fingerprint} size="md" className="text-primary" />
            {label || `${displayAsset} Address`}
          </ModalTitle>
          <ModalDescription id="qr-description">
            Scan the QR code or copy the address below
          </ModalDescription>
        </ModalHeader>

        <div className="space-y-6">
          {/* Apple-quality QR Code with Liquid Glass */}
          <div className="flex justify-center">
            <AppleQR
              value={address}
              size={192}
              variant="glass"
              dotStyle="rounded"
              cornerStyle="extra-rounded"
              shimmer
              alt={`QR code for ${displayAsset} address${displayChain ? ` on ${displayChain}` : ''}`}
            />
          </div>

          {/* Address Display */}
          <div className="space-y-3">
            <div className="text-sm font-medium text-muted-foreground">
              {displayAsset} Address {displayChain && `(${displayChain})`}
            </div>
            <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
              <code className="flex-1 text-sm font-mono break-all">
                {address}
              </code>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2">
            <Button
              onClick={copyAddress}
              variant="outline"
              className="flex-1"
            >
              Copy Address
            </Button>
            <Button
              onClick={shareAddress}
              variant="outline"
              className="flex-1 gap-2"
            >
              <SFIcon icon={sfIcons.send} size="sm" />
              Share
            </Button>
          </div>

          {/* Warning */}
          <div className={cn(
            "text-xs p-3 rounded-lg",
            semanticTokens.warning.bg,
            semanticTokens.warning.fg
          )}>
            Only send {displayAsset} to this address.
            Sending other tokens may result in permanent loss.
          </div>
        </div>
      </Modal>
    </>
  );
};
